"use client";

import { useEffect } from "react";

/**
 * Global hash scroll handler that runs on page load and handles hash changes
 * This component should be included in the root layout to handle initial page load scrolling
 * to hash anchors, especially for content that loads asynchronously.
 *
 * Components can use the `useHashScroll` hook to register their own anchor IDs.
 */
export function HashScrollHandler() {
  useEffect(() => {
    const scrollToHash = (hash: string, retries = 10) => {
      if (!hash) return;

      const element = document.getElementById(hash);
      if (element) {
        // Calculate offset for fixed headers (adjust as needed)
        const headerOffset = 80; // Approximate header height
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
        return true;
      }

      // If element not found and we have retries left, try again
      if (retries > 0) {
        setTimeout(() => {
          scrollToHash(hash, retries - 1);
        }, 100);
      }
      return false;
    };

    // Handle initial page load
    const handleInitialHash = () => {
      const hash = window.location.hash.slice(1);
      if (hash) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          scrollToHash(hash);
        }, 100);
      }
    };

    // Handle hash changes (e.g., from navigation)
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash) {
        scrollToHash(hash);
      }
    };

    // Run on mount
    handleInitialHash();

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);

    // Also listen for popstate (browser back/forward)
    window.addEventListener("popstate", handleInitialHash);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handleInitialHash);
    };
  }, []);

  return null;
}
